import { sceneCard, scenePic, sceneSeg, SCENE_CHECK } from '@flobi/asset-library/tours';

/**
 * The pictures on Artwork's tour cards: a white card floating on Artwork's grainy violet or pink,
 * drawn with the kit's pieces (@flobi/asset-library/tours scenes.js), the way Flow's and Handoff's
 * are. A step names one with `media: "<key>"`; the kit's TourPicture puts the markup in. Static,
 * built from our own words.
 */
const VIOLET = 'artwork';
const PINK = 'artworkPink';

const kv = (label: string, value: string) => `<div class="kv"><span>${label}</span><b>${value}</b></div>`;
const head = (title: string, sub: string) => `<div class="row sp" style="margin-bottom:1.4cqw"><span class="ttl">${title}</span><span class="sub">${sub}</span></div>`;
const foot = (left: string, right: string) => `<div class="row sp" style="margin-top:1.8cqw">${left}${right}</div>`;
const chip = (tone: string, words: string, d = 0.35) => `<span class="chip ${tone} k pop" style="--d:${d}s">${words}</span>`;
const done = (d: number) => chip('g', `${SCENE_CHECK}Done`, d);
const minis = (pics: string[]) =>
  `<div class="row sp">${pics.map((p, i) => `<span class="mini2 k up" style="--d:${(0.15 + i * 0.1).toFixed(2)}s;--y:3cqw">${p}</span>`).join('')}</div>`;
// A chapter's closing card: what it taught, ticked off.
const recap = (tone: string, title: string, items: string[]) => () =>
  sceneCard(tone, 56, `${head(title, 'Done')}${items.map((x, i) => kv(x, done(0.2 + i * 0.12))).join('')}`, '', title);

export const PICTURES: Record<string, () => string> = {
  // Posts, pages, videos.
  welcome: () =>
    sceneCard(VIOLET, 58, `${minis([scenePic(VIOLET), scenePic(PINK, 'wave'), scenePic(VIOLET, 'wave')])}${foot('<span class="sub">Post · Page · Video</span>', chip('b', 'New File'))}`, '', 'welcome'),
  // A file: its page and its name.
  editor: () => sceneCard(PINK, 52, `<div class="thumb">${scenePic(PINK)}</div>${foot('<span class="ttl">Your file</span>', chip('n', 'Saved'))}`, '', 'editor'),
  // The dock's tools.
  design: () =>
    sceneCard(VIOLET, 60, `${sceneSeg(['Select', 'Shape', 'Text', 'Draw'], 1)}<div style="margin-top:1.6cqw">${kv('Shape', 'Drag it out')}${kv('Text', 'Click and type')}${kv('Colour', 'From your brand')}</div>`, '', 'design'),
  designDone: recap(VIOLET, 'First design', ['A shape', 'Its colour', 'Some words']),
  // The rail's kinds.
  add: () => sceneCard(PINK, 56, `${head('Assets', 'Rail')}${kv('Templates', 'Whole pages')}${kv('Graphics', 'Drawings')}${kv('My Stuff', 'Your pictures')}${kv('Brand', 'Logos, colours, type')}`, '', 'add'),
  addDone: recap(PINK, 'On the page', ['Picked on the rail', 'Placed']),
  // The rail's names, as chips: the left edge of the editor.
  rail: () =>
    sceneCard(PINK, 54, `${['Brand', 'Generate', 'Templates', 'Graphics', 'My Stuff'].map((x, i) => `<div class="row" style="margin:0.6cqw 0">${chip(i === 2 ? 'b' : 'n', x, 0.2 + i * 0.08)}</div>`).join('')}`, '', 'rail'),
  // Press a tile, or drag it where it goes.
  place: () => sceneCard(PINK, 52, `<div class="thumb">${scenePic(PINK, 'wave')}</div>${foot('<span class="sub">Press, or drag it in</span>', chip('b', 'Placed'))}`, '', 'place'),
  // Words in, a picture out.
  ai: () => sceneCard(VIOLET, 54, `<div class="thumb">${scenePic(VIOLET)}</div>${kv('Prompt', 'A violet bottle, soft light')}${foot('<span class="sub">Free in the tour</span>', chip('b', 'Generate'))}`, '', 'ai'),
  aiDone: () => sceneCard(VIOLET, 56, `${head('Edit with AI', 'Picture')}${kv('Remove background', 'First')}${kv('Change', 'Say what')}${kv('Adjust', chip('g', 'Free', 0.3))}`, '', 'aiDone'),
  // A recipe and Play.
  animate: () => sceneCard(PINK, 56, `${minis([scenePic(PINK), scenePic(PINK, 'wave'), scenePic(VIOLET)])}${foot('<span class="sub">One press, the whole page</span>', chip('b', 'Preview'))}`, '', 'animate'),
  animateDone: () => sceneCard(PINK, 60, `${sceneSeg(['Comes In', 'Keeps Moving', 'Goes Out'], 0)}<div style="margin-top:1.6cqw">${kv('Pace', 'Slow · Fast')}${kv('Text', 'Word by word')}</div>`, '', 'animateDone'),
  // Together, then anywhere.
  share: () => sceneCard(VIOLET, 56, `${head('Spring post', 'Share')}${kv('Mira', 'Can edit')}${kv('Anyone with the link', 'Can view')}${foot('<span class="sub">PNG · PDF · MP4</span>', chip('b', 'Export'))}`, '', 'share'),
  shareDone: recap(VIOLET, 'Out in the world', ['Shared', 'Exported']),
  // Everything, ticked.
  done: () => sceneCard(PINK, 56, `${head('Artwork', 'All done')}${kv('Design', done(0.2))}${kv('Animate', done(0.32))}${kv('Export', done(0.44))}`, '', 'done'),
};

/** The strips' colours by chapter: violet, with pink for adding and animating. */
const CHAPTER_TONE: Record<string, string> = { add: PINK, animate: PINK };
export const toneOf = (chapterId: string) => CHAPTER_TONE[chapterId] ?? VIOLET;

/** Each tour's first picture, by its Get started step: the "New to Artwork?" band and Tips' path rows. */
const STEP_PICTURE: Record<string, string> = {
  artwork_welcome: 'welcome',
  artwork_first_design: 'design',
  artwork_add: 'add',
  artwork_ai: 'ai',
  artwork_animate: 'animate',
  artwork_share_export: 'share',
};
export const pictureForStep = (actionKey: string | undefined): string | null => {
  const make = actionKey ? PICTURES[STEP_PICTURE[actionKey] ?? ''] : undefined;
  return make ? make() : null;
};
